import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Bot,
  ChevronLeft,
  BadgeCheck,
  Power,
  Pencil,
  Plus,
  Trash2,
  Ban,
  BarChart3,
  User,
  Webhook,
  LayoutGrid,
  Palette,
  Images,
  Briefcase,
  Handshake,
  Gamepad2,
  KeyRound,
} from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { PERMISSIONS, isSuperAdmin } from '@/lib/permissions'
import { InfoRow, MonoValue } from '@/components/DataDisplay'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { DangerDialog } from '@/components/DangerDialog'
import { Button } from '@/components/ui/button'
import { Input, Textarea, Field as FormField } from '@/components/ui/input'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { fetchBotConfig, editBot, type AdminBotConfig, type BotCommand } from './botApi'
import { banUser } from '@/lib/adapters'

interface Draft {
  first_name: string
  description: string
  about: string
  commands: BotCommand[]
}

/**
 * A5.02 — Bot kartasi. `GET /admin/bots/:id` to'liq config (nom/rasmiy/holat/tavsif/
 * buyruqlar). Super-admin bo'lsa: A5.10 tahrir-forma (nom/tavsif/bot-haqida/buyruqlar
 * → `POST /admin/bots/:id`), A5.14 rasmiy-belgi, A5.09 yoq/o'chir (DangerDialog L2).
 * Egasi/webhook/token/statistika = bot-platforma gateway'i (A5.03–08).
 */
export function BotCard() {
  const t = useT()
  const { can, identity } = useAuth()
  const { toast } = useToast()
  const { id = '' } = useParams()
  const [bot, setBot] = useState<AdminBotConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [officialTarget, setOfficialTarget] = useState<AdminBotConfig | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<AdminBotConfig | null>(null)
  const [blockTarget, setBlockTarget] = useState<AdminBotConfig | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchBotConfig(Number(id), ac.signal)
      .then((b) => {
        if (alive) setBot(b)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const canEdit = isSuperAdmin(identity)
  const editing = draft !== null

  function startEdit(b: AdminBotConfig) {
    setDraft({
      first_name: b.first_name ?? '',
      description: b.description ?? '',
      about: b.about ?? '',
      commands: b.commands.map((c) => ({ ...c })),
    })
  }

  const patchCmd = (i: number, patch: Partial<BotCommand>) =>
    setDraft((d) =>
      d ? { ...d, commands: d.commands.map((c, j) => (j === i ? { ...c, ...patch } : c)) } : d,
    )

  async function save() {
    if (!draft || !bot) return
    setSaving(true)
    try {
      const name = draft.first_name.trim()
      await editBot(bot.id, {
        ...(name ? { first_name: name } : {}),
        description: draft.description,
        about: draft.about,
        commands: draft.commands
          .map((c) => ({ command: c.command.trim(), description: c.description.trim() }))
          .filter((c) => c.command),
      })
      toast(t.bots.actionDone, { tone: 'success' })
      setDraft(null)
      setReload((n) => n + 1)
    } catch (e) {
      toast(t.bots.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setSaving(false)
    }
  }

  const sectionLink = 'inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary'
  const actions = (
    <div className="flex flex-wrap items-center gap-1.5">
      <Link to={`/bots/${id}/stats`} className={sectionLink}>
        <BarChart3 size={13} />
        {t.bots.statsTitle}
      </Link>
      <Link to={`/bots/${id}/owner`} className={sectionLink}>
        <User size={13} />
        {t.bots.ownerTitle}
      </Link>
      <Link to={`/bots/${id}/webhook`} className={sectionLink}>
        <Webhook size={13} />
        {t.bots.webhookTitle}
      </Link>
      <Link to={`/bots/${id}/apps`} className={sectionLink}>
        <LayoutGrid size={13} />
        {t.bots.appsTitle}
      </Link>
      <Link to={`/bots/${id}/app-settings`} className={sectionLink}>
        <Palette size={13} />
        {t.bots.appSettingsTitle}
      </Link>
      <Link to={`/bots/${id}/preview-media`} className={sectionLink}>
        <Images size={13} />
        {t.bots.previewMediaTitle}
      </Link>
      <Link to={`/bots/${id}/business`} className={sectionLink}>
        <Briefcase size={13} />
        {t.bots.businessTitle}
      </Link>
      <Link to={`/bots/${id}/affiliate`} className={sectionLink}>
        <Handshake size={13} />
        {t.bots.affiliateTitle}
      </Link>
      <Link to={`/bots/${id}/games`} className={sectionLink}>
        <Gamepad2 size={13} />
        {t.bots.gamesTitle}
      </Link>
      <Link to={`/bots/${id}/token`} className={sectionLink}>
        <KeyRound size={13} />
        {t.bots.tokenTitle}
      </Link>
      <Link
        to="/bots"
        className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
      >
        <ChevronLeft size={13} />
        {t.nav.bots}
      </Link>
    </div>
  )

  return (
    <>
      <PageHeader
        title={bot ? bot.first_name || (bot.username ? `@${bot.username}` : `#${bot.id}`) : t.bots.card}
        screenId="A5.02"
        actions={actions}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !bot ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : editing && draft ? (
          <div className="max-w-xl space-y-3 rounded-lg border border-border-default bg-surface-2 p-4">
            <FormField label={t.col.name}>
              <Input
                value={draft.first_name}
                maxLength={64}
                onChange={(e) => setDraft((d) => (d ? { ...d, first_name: e.target.value } : d))}
              />
            </FormField>
            <FormField label={t.bots.descLabel}>
              <Textarea
                value={draft.description}
                onChange={(e) => setDraft((d) => (d ? { ...d, description: e.target.value } : d))}
              />
            </FormField>
            <FormField label={t.bots.aboutLabel}>
              <Textarea
                value={draft.about}
                onChange={(e) => setDraft((d) => (d ? { ...d, about: e.target.value } : d))}
              />
            </FormField>
            <FormField label={t.bots.commandsLabel}>
              <div className="space-y-2">
                {draft.commands.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      className="w-40"
                      mono
                      value={c.command}
                      placeholder={t.bots.cmdName}
                      onChange={(e) => patchCmd(i, { command: e.target.value })}
                    />
                    <Input
                      className="flex-1"
                      value={c.description}
                      placeholder={t.bots.descLabel}
                      onChange={(e) => patchCmd(i, { description: e.target.value })}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t.common.delete}
                      onClick={() =>
                        setDraft((d) => (d ? { ...d, commands: d.commands.filter((_, j) => j !== i) } : d))
                      }
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setDraft((d) =>
                      d ? { ...d, commands: [...d.commands, { command: '', description: '' }] } : d,
                    )
                  }
                >
                  <Plus size={14} />
                  {t.bots.addCommand}
                </Button>
              </div>
            </FormField>
            <div className="flex gap-2 pt-1">
              <Button variant="primary" disabled={saving} onClick={save}>
                {saving ? t.common.saving : t.common.save}
              </Button>
              <Button variant="default" disabled={saving} onClick={() => setDraft(null)}>
                {t.common.cancel}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="max-w-xl rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-3 flex items-center gap-2">
                <StatusBadge status={bot.revoked ? 'banned' : 'active'} />
                <Tag>bot</Tag>
                {bot.is_official && <Tag>official</Tag>}
              </div>
              <Field label={t.col.id}>
                <MonoValue value={String(bot.id)} />
              </Field>
              <Field label={t.col.name}>{bot.first_name || '—'}</Field>
              <Field label={t.col.username}>{bot.username ? `@${bot.username}` : '—'}</Field>
              <Field label={t.bots.official}>{bot.is_official ? '✓' : '—'}</Field>
              <Field label={t.bots.statusLabel}>{bot.revoked ? t.bots.disabled : t.bots.active}</Field>
              <Field label={t.bots.descLabel}>{bot.description || '—'}</Field>
              <Field label={t.bots.aboutLabel}>{bot.about || '—'}</Field>
              <Field label={t.bots.commandsLabel}>
                {bot.commands.length ? (
                  <div className="space-y-1">
                    {bot.commands.map((c) => (
                      <div key={c.command} className="flex gap-2 text-sm">
                        <code className="text-accent">/{c.command}</code>
                        <span className="text-secondary">{c.description}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  t.bots.noCommands
                )}
              </Field>
            </div>

            {canEdit ? (
              <div className="mt-3 flex max-w-xl flex-wrap gap-2">
                <ActionBtn icon={<Pencil size={14} />} onClick={() => startEdit(bot)}>
                  {t.bots.edit}
                </ActionBtn>
                <ActionBtn icon={<BadgeCheck size={14} />} onClick={() => setOfficialTarget(bot)}>
                  {bot.is_official ? t.bots.unmakeOfficial : t.bots.makeOfficial}
                </ActionBtn>
                <ActionBtn danger={!bot.revoked} icon={<Power size={14} />} onClick={() => setRevokeTarget(bot)}>
                  {bot.revoked ? t.bots.enable : t.bots.disable}
                </ActionBtn>
                <ActionBtn danger icon={<Ban size={14} />} onClick={() => setBlockTarget(bot)}>
                  {t.bots.block}
                </ActionBtn>
              </div>
            ) : (
              <div className="mt-3 flex max-w-xl items-start gap-2 rounded-lg border border-border-subtle bg-surface-1 p-3 text-sm text-tertiary">
                <Bot size={16} className="mt-0.5 shrink-0" />
                <span>{t.bots.superadminOnly}</span>
              </div>
            )}
          </>
        )}
      </div>

      {officialTarget && (
        <DangerDialog
          open
          onClose={() => setOfficialTarget(null)}
          screenId="A5.14"
          level={2}
          title={officialTarget.is_official ? t.bots.unofficialTitle : t.bots.officialTitle}
          description={officialTarget.is_official ? t.bots.unofficialDesc : t.bots.officialDesc}
          confirmLabel={officialTarget.is_official ? t.bots.unmakeOfficial : t.bots.makeOfficial}
          onConfirm={async () => {
            try {
              await editBot(officialTarget.id, { is_official: !officialTarget.is_official })
              toast(t.bots.actionDone, { tone: 'success' })
              setReload((n) => n + 1)
            } catch (e) {
              toast(t.bots.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e // dialog ochiq qolsin
            }
          }}
        />
      )}

      {revokeTarget && (
        <DangerDialog
          open
          onClose={() => setRevokeTarget(null)}
          screenId="A5.09"
          level={2}
          title={revokeTarget.revoked ? t.bots.enableTitle : t.bots.disableTitle}
          description={revokeTarget.revoked ? t.bots.enableDesc : t.bots.disableDesc}
          confirmLabel={revokeTarget.revoked ? t.bots.enable : t.bots.disable}
          onConfirm={async () => {
            try {
              await editBot(revokeTarget.id, { revoked: !revokeTarget.revoked })
              toast(t.bots.actionDone, { tone: 'success' })
              setReload((n) => n + 1)
            } catch (e) {
              toast(t.bots.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}

      {blockTarget && (
        <DangerDialog
          open
          onClose={() => setBlockTarget(null)}
          screenId="A5.12"
          level={3}
          title={t.bots.blockTitle}
          description={t.bots.blockDesc}
          confirmLabel={t.bots.block}
          objectName={blockTarget.username ? `@${blockTarget.username}` : `#${blockTarget.id}`}
          onConfirm={async ({ reason }) => {
            try {
              await banUser(blockTarget.id, reason)
              toast(t.bots.actionDone, { tone: 'success' })
              setReload((n) => n + 1)
            } catch (e) {
              toast(t.bots.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}

function ActionBtn({
  icon,
  children,
  onClick,
  danger,
}: {
  icon: ReactNode
  children: ReactNode
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${
        danger
          ? 'border-danger/40 text-danger hover:bg-danger-subtle'
          : 'border-border-default bg-surface-2 text-primary hover:bg-surface-3'
      }`}
    >
      {icon}
      {children}
    </button>
  )
}
